import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Messages } from "primereact/messages";
import { ProgressBar } from "primereact/progressbar";
import Container from "react-bootstrap/Container";
import { useAuth } from "../contexts/AuthContext";
import walletApi from "../api/walletApi";
import AccessState from "./AccessState";
import usePageAccessRedirect from "../hooks/usePageAccessRedirect";
import "./LocationWalletPortal.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emptyForm = () => ({
  email: "",
  isMultiUse: false,
  maxUses: 1,
  durationMinutes: 0,
  durationHours: 0,
  durationDays: 0,
  memberNumber: ""
});
const DEFAULT_DURATION_SHORTCUTS = [
  { id: "30-minutes", label: "30 minutes", minutes: 30 },
  { id: "1-hour", label: "1 hour", minutes: 60 },
  { id: "1-day", label: "1 day", minutes: 1440 }
];
const ACCESS_POINT_ROW_BATCH_SIZE = 3;
const durationParts = totalMinutes => {
  const value = Number(totalMinutes) || 0;
  return {
    days: Math.floor(value / 1440),
    hours: Math.floor((value % 1440) / 60),
    minutes: value % 60
  };
};
const durationInMinutes = duration =>
  Number(duration.durationMinutes || 0) +
  Number(duration.durationHours || 0) * 60 +
  Number(duration.durationDays || 0) * 1440;

const LocationWalletPortal = () => {
  const { accessContext } = useAuth();
  const locationId = Number(
    accessContext && accessContext.claims && accessContext.claims.locationId
  );
  const msgs = useRef(null);
  const accessPointsListRef = useRef(null);
  const {
    accessIssue,
    clearAccessIssue,
    handleAccessError
  } = usePageAccessRedirect({
    deniedTitle: "Wallet Portal Unavailable",
    deniedMessage: "You do not have access to the Wallet Passes portal.",
    deniedRedirectTo: "/support/tiles"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [accessPoints, setAccessPoints] = useState([]);
  const [selectedAccessPointIds, setSelectedAccessPointIds] = useState([]);
  const [visibleAccessPointRows, setVisibleAccessPointRows] = useState(ACCESS_POINT_ROW_BATCH_SIZE);
  const [accessPointColumnCount, setAccessPointColumnCount] = useState(1);
  const [form, setForm] = useState(emptyForm());
  const [createdPass, setCreatedPass] = useState(null);
  const [customShortcuts, setCustomShortcuts] = useState([]);
  const [showShortcutDialog, setShowShortcutDialog] = useState(false);
  const [shortcutName, setShortcutName] = useState("");
  const maxUsesLimit =
    availability && availability.limitByMaxUses
      ? Number(availability.maxUsesLimit)
      : null;
  const maxDurationMinutes =
    availability && availability.limitByMaxDuration
      ? Number(availability.maxDurationMinutes)
      : null;
  const maximumDuration = durationParts(maxDurationMinutes);
  const requestedDurationMinutes = durationInMinutes(form);
  const shortcutStorageKey = `wallet-pass-duration-shortcuts-${locationId}`;
  const allShortcuts = DEFAULT_DURATION_SHORTCUTS.concat(customShortcuts);
  const availableShortcuts = allShortcuts.filter(
    shortcut =>
      !Number.isFinite(maxDurationMinutes) ||
      maxDurationMinutes <= 0 ||
      shortcut.minutes <= maxDurationMinutes
  );
  const availableAccessPointIds = accessPoints.map((accessPoint) => Number(accessPoint.id)).filter(Number.isFinite);
  const areAllAccessPointsSelected = availableAccessPointIds.length > 0 && availableAccessPointIds.every((accessPointId) => selectedAccessPointIds.indexOf(accessPointId) !== -1);
  const accessPointRowCount = Math.ceil(accessPoints.length / accessPointColumnCount);
  const hasMoreAccessPointRows = accessPointRowCount > visibleAccessPointRows;
  const canShowFewerAccessPointRows = visibleAccessPointRows > ACCESS_POINT_ROW_BATCH_SIZE;
  const walletUnavailableMessage = availability && availability.customerWalletPassesEnabled === false
    ? "Wallet passes are disabled for this customer."
    : availability && availability.locationWalletPassesEnabled === false
    ? "Wallet passes are disabled for this location."
    : "Wallet passes are not enabled for this customer.";

  const showError = (error, fallback) => {
    if (handleAccessError(error)) return;
    const data = error && error.response && error.response.data;
    const detail =
      typeof data === "string" && data.trim()
        ? data
        : (data && (data.detail || data.title)) ||
          (error && error.message) ||
          fallback;
    if (msgs.current)
      msgs.current.show({
        severity: "error",
        summary: "",
        detail,
        sticky: true
      });
  };

  useEffect(() => {
    const load = async () => {
      if (!Number.isFinite(locationId) || locationId <= 0) {
        setLoading(false);
        return;
      }
      try {
        clearAccessIssue();
        const result = await walletApi.getLocationAvailability(locationId);
        setAvailability(result);
        if (result && result.isEnabled) {
          try {
            const resultAccessPoints = await walletApi.getLocationAccessPoints(locationId);
            setAccessPoints(Array.isArray(resultAccessPoints) ? resultAccessPoints : []);
            setVisibleAccessPointRows(ACCESS_POINT_ROW_BATCH_SIZE);
          } catch (error) {
            setAccessPoints([]);
            showError(error, "Failed to load access points.");
          }
        } else {
          setAccessPoints([]);
          setSelectedAccessPointIds([]);
        }
      } catch (error) {
        showError(error, "Failed to load Wallet Passes availability.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [locationId, clearAccessIssue]);

  useEffect(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(shortcutStorageKey) || "[]"
      );
      setCustomShortcuts(
        Array.isArray(saved)
          ? saved.filter(
              shortcut =>
                shortcut &&
                typeof shortcut.label === "string" &&
                Number(shortcut.minutes) > 0
            )
          : []
      );
    } catch (_) {
      setCustomShortcuts([]);
    }
  }, [shortcutStorageKey]);

  useEffect(() => {
    const updateAccessPointColumnCount = () => {
      const list = accessPointsListRef.current;
      if (!list) return;
      const columns = window.getComputedStyle(list).gridTemplateColumns
        .split(" ")
        .filter((track) => Number.parseFloat(track) > 0).length;
      setAccessPointColumnCount(Math.max(1, columns));
    };
    updateAccessPointColumnCount();
    window.addEventListener("resize", updateAccessPointColumnCount);
    return () => window.removeEventListener("resize", updateAccessPointColumnCount);
  }, [accessPoints]);

  const validate = () => {
    if (!EMAIL_PATTERN.test(String(form.email || "").trim()))
      return "Enter a valid email address.";
    if (selectedAccessPointIds.length === 0)
      return "At least one access point must be selected.";
    if (
      !Number.isInteger(requestedDurationMinutes) ||
      requestedDurationMinutes <= 0
    )
      return "Maximum usage time must be greater than zero.";
    if (!form.isMultiUse && Number(form.maxUses) !== 1)
      return "Single-use passes must have one use.";
    if (form.isMultiUse && Number(form.maxUses) <= 1)
      return "Multi-use passes must have more than one use.";
    if (
      Number.isFinite(maxUsesLimit) &&
      maxUsesLimit > 0 &&
      Number(form.maxUses) > maxUsesLimit
    )
      return `Number of uses cannot exceed ${maxUsesLimit}.`;
    if (
      Number.isFinite(maxDurationMinutes) &&
      maxDurationMinutes > 0 &&
      requestedDurationMinutes > maxDurationMinutes
    )
      return "Maximum usage time cannot exceed the maximum allowed duration.";
    return null;
  };

  const applyDuration = minutes => {
    const parts = durationParts(minutes);
    setForm({
      ...form,
      durationMinutes: parts.minutes,
      durationHours: parts.hours,
      durationDays: parts.days
    });
  };

  const saveShortcut = () => {
    const label = String(shortcutName || "").trim();
    if (!label) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Enter a name for the time shortcut."
      });
      return;
    }
    if (requestedDurationMinutes <= 0) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "Set a maximum usage time before saving a shortcut."
      });
      return;
    }
    if (
      Number.isFinite(maxDurationMinutes) &&
      maxDurationMinutes > 0 &&
      requestedDurationMinutes > maxDurationMinutes
    ) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: "The shortcut cannot exceed the maximum allowed duration."
      });
      return;
    }
    const next = customShortcuts.concat({
      id: String(Date.now()),
      label,
      minutes: requestedDurationMinutes
    });
    localStorage.setItem(shortcutStorageKey, JSON.stringify(next));
    setCustomShortcuts(next);
    setShortcutName("");
    setShowShortcutDialog(false);
  };

  const create = async () => {
    const validationError = validate();
    if (validationError) {
      msgs.current.show({
        severity: "error",
        summary: "",
        detail: validationError
      });
      return;
    }
    try {
      setSaving(true);
      const memberNumber = String(form.memberNumber || "").trim();
      const result = await walletApi.createPass(locationId, {
        email: form.email.trim(),
        isMultiUse: form.isMultiUse,
        maxUses: form.isMultiUse ? Number(form.maxUses) : 1,
        expiresAtUtc: new Date(
          Date.now() + requestedDurationMinutes * 60000
        ).toISOString(),
        overrideLms: !memberNumber,
        memberNumber: memberNumber || null,
        accessPointIds: selectedAccessPointIds
      });
      setCreatedPass(result);
      setForm(emptyForm());
      setSelectedAccessPointIds([]);
      msgs.current.show({
        severity: "success",
        summary: "",
        detail: "Wallet pass created successfully."
      });
    } catch (error) {
      showError(error, "Failed to create wallet pass.");
    } finally {
      setSaving(false);
    }
  };

  const copyPassUrl = () => {
    if (createdPass && createdPass.passUrl) {
      navigator.clipboard.writeText(createdPass.passUrl);
      msgs.current.show({
        severity: "success",
        summary: "",
        detail: "Pass link copied to clipboard."
      });
    }
  };
  if (accessIssue) return <AccessState {...accessIssue} />;
  if (!Number.isFinite(locationId) || locationId <= 0)
    return (
      <AccessState
        title="Wallet Portal Unavailable"
        message="No location is associated with your account."
        actionLabel="Go To Support Home"
        actionTo="/support"
      />
    );

  return (
    <Card className="justify-content-md-center mt-5">
      <Container fluid="md">
        <h4>Wallet Passes</h4>
        <Messages ref={msgs} />
        {loading ? (
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        ) : !availability || !availability.isEnabled ? (
          <p>{walletUnavailableMessage}</p>
        ) : (
          <>
            <div className="p-fluid p-formgrid p-grid">
              <div className="p-field p-col-12 p-md-6">
                <label htmlFor="walletEmail">Email address</label>
                <InputText
                  id="walletEmail"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="p-field p-col-12 p-md-6">
                <label htmlFor="walletType">Pass type</label>
                <Dropdown
                  id="walletType"
                  value={form.isMultiUse}
                  options={[
                    { label: "Single use", value: false },
                    {
                      label: "Multi use",
                      value: true,
                      disabled:
                        Number.isFinite(maxUsesLimit) && maxUsesLimit < 2
                    }
                  ]}
                  optionDisabled="disabled"
                  onChange={e =>
                    setForm({
                      ...form,
                      isMultiUse: e.value,
                      maxUses: e.value ? 2 : 1
                    })
                  }
                />
              </div>
              {Number.isFinite(maxUsesLimit) && maxUsesLimit > 0 && (
                <div className="p-col-12">
                  <small>Maximum allowed uses: {maxUsesLimit}</small>
                </div>
              )}
              {form.isMultiUse && (
                <div className="p-field p-col-12 p-md-6">
                  <label htmlFor="walletUses">Number of passes/uses</label>
                  <InputNumber
                    id="walletUses"
                    value={form.maxUses}
                    min={2}
                    max={
                      Number.isFinite(maxUsesLimit) && maxUsesLimit > 0
                        ? maxUsesLimit
                        : undefined
                    }
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    useGrouping={false}
                    onValueChange={e => setForm({ ...form, maxUses: e.value })}
                  />
                </div>
              )}
              <div className="p-field p-col-12 wallet-access-points-field">
                <div className="wallet-access-points-header">
                  <label>Access points</label>
                  <Button
                    label={areAllAccessPointsSelected ? "Unselect all" : "Select all"}
                    className="p-button-text p-button-sm"
                    onClick={() => setSelectedAccessPointIds(areAllAccessPointsSelected ? [] : availableAccessPointIds)}
                    disabled={accessPoints.length === 0}
                  />
                </div>
                {accessPoints.length === 0 ? (
                  <small>No access points are available for this location.</small>
                ) : (
                  <div
                    ref={accessPointsListRef}
                    className="wallet-access-points"
                    style={{ maxHeight: `${visibleAccessPointRows * 3.75 - 0.5}rem` }}
                  >
                    {accessPoints.map((accessPoint) => {
                      const accessPointId = Number(accessPoint.id);
                      const label = String(accessPoint.gate || accessPoint.tile || `Access point ${accessPointId}`);
                      return (
                        <div key={accessPointId} className="wallet-access-point">
                          <Checkbox
                            inputId={`wallet-access-point-${accessPointId}`}
                            checked={selectedAccessPointIds.indexOf(accessPointId) !== -1}
                            onChange={(event) => setSelectedAccessPointIds((current) => event.checked ? current.concat(accessPointId) : current.filter((id) => id !== accessPointId))}
                          />
                          <label htmlFor={`wallet-access-point-${accessPointId}`}>{label}</label>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(hasMoreAccessPointRows || canShowFewerAccessPointRows) && (
                  <Button
                    label={hasMoreAccessPointRows ? "Show more" : "Show less"}
                    className="p-button-text p-button-sm wallet-access-points-toggle"
                    onClick={() => setVisibleAccessPointRows(hasMoreAccessPointRows ? (current) => current + ACCESS_POINT_ROW_BATCH_SIZE : ACCESS_POINT_ROW_BATCH_SIZE)}
                  />
                )}
                <small className="wallet-access-points-hint">Select at least one access point.</small>
              </div>
              <div className="p-field p-col-12 wallet-duration-field">
                <label>Maximum usage time</label>
                <div className="wallet-duration-row">
                  <div className="wallet-duration-input">
                    <InputNumber
                      id="walletDurationMinutes"
                      value={form.durationMinutes}
                      min={0}
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      useGrouping={false}
                      onValueChange={e =>
                        setForm({ ...form, durationMinutes: e.value })
                      }
                    />
                    <label htmlFor="walletDurationMinutes">minutes</label>
                  </div>
                  <div className="wallet-duration-input">
                    <InputNumber
                      id="walletDurationHours"
                      value={form.durationHours}
                      min={0}
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      useGrouping={false}
                      onValueChange={e =>
                        setForm({ ...form, durationHours: e.value })
                      }
                    />
                    <label htmlFor="walletDurationHours">hours</label>
                  </div>
                  <div className="wallet-duration-input">
                    <InputNumber
                      id="walletDurationDays"
                      value={form.durationDays}
                      min={0}
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      useGrouping={false}
                      onValueChange={e =>
                        setForm({ ...form, durationDays: e.value })
                      }
                    />
                    <label htmlFor="walletDurationDays">days</label>
                  </div>
                  <Button
                    label="Save as shortcut"
                    icon="pi pi-bookmark"
                    className="p-button-text p-button-sm wallet-save-shortcut"
                    onClick={() => setShowShortcutDialog(true)}
                  />
                </div>
                {Number.isFinite(maxDurationMinutes) &&
                  maxDurationMinutes > 0 && (
                    <small className="wallet-duration-limit">
                      Maximum allowed duration: {maximumDuration.days} days{" "}
                      {maximumDuration.hours} hours {maximumDuration.minutes}{" "}
                      minutes
                    </small>
                  )}
                <div className="wallet-duration-shortcuts">
                  {availableShortcuts.map(shortcut => (
                    <Button
                      key={shortcut.id}
                      label={shortcut.label}
                      className="p-button-outlined p-button-sm"
                      onClick={() => applyDuration(shortcut.minutes)}
                    />
                  ))}
                </div>
              </div>
              <div className="p-field p-col-12 p-md-6">
                <label htmlFor="walletMember">
                  Add member/card number, if not a member, leave empty
                </label>
                <InputText
                  id="walletMember"
                  value={form.memberNumber}
                  onChange={e =>
                    setForm({ ...form, memberNumber: e.target.value })
                  }
                />
              </div>
              <div className="p-col-12">
                <Button
                  label="Create Wallet Pass"
                  icon="pi pi-plus"
                  onClick={create}
                  loading={saving}
                  disabled={selectedAccessPointIds.length === 0}
                />
              </div>
            </div>
            {createdPass && (
              <div className="mt-3">
                <InputText
                  value={createdPass.passUrl || ""}
                  readOnly
                  style={{ width: "min(100%, 44rem)" }}
                />
                <Button
                  icon="pi pi-copy"
                  className="p-button-text ml-2"
                  onClick={copyPassUrl}
                  tooltip="Copy pass link"
                />
              </div>
            )}
          </>
        )}
        <Dialog
          header="Save Time Shortcut"
          visible={showShortcutDialog}
          onHide={() => setShowShortcutDialog(false)}
          footer={
            <>
              <Button
                label="Cancel"
                className="p-button-text"
                onClick={() => setShowShortcutDialog(false)}
              />
              <Button label="Save" icon="pi pi-save" onClick={saveShortcut} />
            </>
          }
        >
          <div className="p-fluid">
            <label htmlFor="walletShortcutName">Shortcut name</label>
            <InputText
              id="walletShortcutName"
              value={shortcutName}
              onChange={e => setShortcutName(e.target.value)}
            />
          </div>
        </Dialog>
      </Container>
    </Card>
  );
};

export default LocationWalletPortal;
